import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import ContactModal from '../components/ContactModal';
import ExitIntentModal from '../components/ExitIntentModal';
import {
    ClipboardCheck, Thermometer, AlertTriangle, Shield, BookOpen,
    UtensilsCrossed, GraduationCap, BarChart3, Printer, Tag,
    ChevronRight, Check, ArrowRight, Gauge, Package, Bluetooth, Smartphone, Mail
} from 'lucide-react';

const LOGO_URL = "/logo.svg";
const HERO_IMG = "https://images.unsplash.com/photo-1666479258732-5ea17469b610?w=1200&q=80";
const KITCHEN_IMG = "https://images.unsplash.com/photo-1763219805214-91fa634e6006?w=800&q=80";
const CHEF_IMG = "https://images.unsplash.com/photo-1708915965975-2a950db0e215?w=800&q=80";
const TABLET_IMG = "https://images.pexels.com/photos/33266834/pexels-photo-33266834.jpeg?auto=compress&cs=tinysrgb&w=800";

const features = [
    { icon: ClipboardCheck, title: 'Daily Diary', desc: 'Opening, closing and cleaning checklists completed digitally every day.' },
    { icon: Thermometer, title: 'Temperature Logs', desc: 'Record fridge, freezer and probe temperatures with instant alerts.' },
    { icon: BookOpen, title: 'Safe Methods', desc: 'All seven SFBB safe methods documented and ready for your team.' },
    { icon: UtensilsCrossed, title: 'Allergen Management', desc: 'Full UK 14 allergen matrix with menu item tracking.' },
    { icon: AlertTriangle, title: 'Incident Reporting', desc: 'Log and resolve food safety incidents with corrective actions.' },
    { icon: GraduationCap, title: 'Training Records', desc: 'Track staff training, certifications and expiry dates.' },
    { icon: Gauge, title: 'Probe Calibration', desc: 'Record thermometer calibrations with pass/fail and sign-off.' },
    { icon: Package, title: 'Delivery Checks', desc: 'Log delivery acceptance with temperature and quality checks per item.' },
    { icon: Tag, title: 'Storage Labels', desc: 'Print date labels with allergens directly from your phone.' },
    { icon: BarChart3, title: 'Compliance Reports', desc: 'Generate and export PDF compliance reports for inspections.' },
    { icon: Shield, title: '4-Weekly Reviews', desc: 'Scheduled SFBB reviews to maintain ongoing compliance.' },
    { icon: Smartphone, title: 'Works on Any Device', desc: 'Use on phones, tablets or desktops — no app install needed.' },
];

const hardware = [
    {
        title: 'Bluetooth Label Printer',
        desc: 'Print food storage date labels with allergens directly from the app. Labels print in seconds via Bluetooth — no setup needed.',
        features: ['One-tap print from your phone', 'Thermal labels — no ink required', '50mm wide labels'],
        img: '🏷️',
    },
    {
        title: 'Bluetooth Probe Thermometer',
        desc: 'Take accurate temperature readings that sync directly into your logs. Eliminates manual data entry errors.',
        features: ['Auto-logs to the app', 'Food-grade stainless probe', 'Instant readings'],
        img: '🌡️',
    },
    {
        title: 'Barcode Scanner',
        desc: 'Scan supplier delivery barcodes to speed up your delivery acceptance checks and reduce paperwork.',
        features: ['Speeds up delivery logging', 'Bluetooth wireless', 'Works with any smartphone'],
        img: '📱',
    },
];

export default function LandingPage() {
    const [contactOpen, setContactOpen] = useState(false);
    const [contactSubject, setContactSubject] = useState('');

    const openEnquiry = (subject) => {
        setContactSubject(subject);
        setContactOpen(true);
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100">
            {/* Sticky Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50" data-testid="landing-header">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <img src={LOGO_URL} alt="Food Safe Hub" className="h-9" />
                    </Link>
                    <nav className="hidden md:flex items-center gap-8 text-sm text-slate-400">
                        <a href="#features" className="hover:text-amber-400 transition-colors">Features</a>
                        <a href="#pricing" className="hover:text-amber-400 transition-colors">Pricing</a>
                        <a href="#hardware" className="hover:text-amber-400 transition-colors">Hardware</a>
                        <button onClick={() => openEnquiry('')} className="hover:text-amber-400 transition-colors" data-testid="header-contact-us">Contact</button>
                    </nav>
                    <div className="flex items-center gap-3">
                        <Link to="/login">
                            <Button variant="ghost" className="text-slate-300 hover:text-amber-400 text-sm h-9" data-testid="header-sign-in">Sign In</Button>
                        </Link>
                        <Link to="/register">
                            <Button className="brand-btn font-semibold text-sm h-9 px-5" data-testid="header-get-started">
                                Get Started
                            </Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero — Split Layout */}
            <section className="pt-16" data-testid="hero-section">
                <div className="grid lg:grid-cols-2 min-h-[calc(100vh-4rem)]">
                    {/* Left: Photo */}
                    <div className="relative hidden lg:block">
                        <img src={HERO_IMG} alt="Professional kitchen" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-slate-950" />
                        <div className="absolute inset-0 bg-slate-950/30" />
                    </div>

                    {/* Right: Content */}
                    <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-16 lg:py-0">
                        <div className="max-w-lg">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium mb-6">
                                <Shield className="h-3.5 w-3.5" /> Built on UK SFBB guidance
                            </div>

                            <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-4">
                                Food safety compliance,
                                <span className="text-amber-400"> simplified.</span>
                            </h1>

                            <p className="text-lg text-slate-400 leading-relaxed mb-8">
                                Digital checklists, temperature logs, allergen tracking, staff training and inspection-ready reports — all in one platform designed for UK food businesses.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-3 mb-8">
                                <Link to="/register">
                                    <Button className="brand-btn font-bold text-base h-12 px-8 w-full sm:w-auto" data-testid="hero-cta">
                                        Start Free Trial <ArrowRight className="h-5 w-5 ml-2" />
                                    </Button>
                                </Link>
                                <a href="#features">
                                    <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 h-12 px-6 w-full sm:w-auto">
                                        See Features
                                    </Button>
                                </a>
                            </div>

                            <div className="flex items-center gap-6 text-sm text-slate-500">
                                <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-400" /> No credit card required</span>
                                <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-400" /> Cancel anytime</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mobile hero image */}
                <div className="lg:hidden relative h-64 -mt-4">
                    <img src={HERO_IMG} alt="Professional kitchen" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
                </div>
            </section>

            {/* Social Proof Bar */}
            <section className="border-y border-slate-800/50 bg-slate-900/30">
                <div className="max-w-7xl mx-auto px-6 py-8 flex flex-wrap items-center justify-center gap-x-12 gap-y-4 text-center">
                    <div><p className="text-2xl font-bold text-amber-400">12+</p><p className="text-xs text-slate-500 mt-0.5">SFBB Modules</p></div>
                    <div className="w-px h-8 bg-slate-800 hidden sm:block" />
                    <div><p className="text-2xl font-bold text-slate-100">UK 14</p><p className="text-xs text-slate-500 mt-0.5">Allergens Tracked</p></div>
                    <div className="w-px h-8 bg-slate-800 hidden sm:block" />
                    <div><p className="text-2xl font-bold text-slate-100">PDF</p><p className="text-xs text-slate-500 mt-0.5">Report Export</p></div>
                    <div className="w-px h-8 bg-slate-800 hidden sm:block" />
                    <div><p className="text-2xl font-bold text-emerald-400">24/7</p><p className="text-xs text-slate-500 mt-0.5">Access Anywhere</p></div>
                </div>
            </section>

            {/* Features Grid */}
            <section id="features" className="py-20 sm:py-28" data-testid="features-section">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-14">
                        <h2 className="text-3xl sm:text-4xl font-bold mb-3">Everything you need for SFBB compliance</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">Digital tools to record, manage and evidence your food safety processes — built around the FSA's Safer Food, Better Business framework.</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {features.map((f) => (
                            <div key={f.title} className="group p-5 rounded-xl border border-slate-800/60 bg-slate-900/30 hover:bg-slate-900/60 hover:border-amber-500/20 transition-all" data-testid={`feature-card-${f.title.toLowerCase().replace(/\s/g, '-')}`}>
                                <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-amber-500/10 text-amber-400 shrink-0 group-hover:bg-amber-500/20 transition-colors">
                                        <f.icon className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-slate-200 mb-1">{f.title}</h3>
                                        <p className="text-xs text-slate-400 leading-relaxed">{f.desc}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Split Section — Why Digital */}
            <section className="py-16 bg-slate-900/40 border-y border-slate-800/50">
                <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
                    <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                        <img src={TABLET_IMG} alt="Digital checklist on tablet" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent" />
                    </div>
                    <div>
                        <h2 className="text-3xl font-bold mb-4">Replace the paper. Keep the control.</h2>
                        <p className="text-slate-400 mb-6 leading-relaxed">
                            No more lost diaries, illegible logs or missing records. Food Safe Hub gives your team a simple digital workflow that captures compliance data in real time — accessible from any device, ready for inspection at a moment's notice.
                        </p>
                        <ul className="space-y-3">
                            {[
                                'Instant access to all records from any device',
                                'Automatic date/time stamps on every entry',
                                'Manager sign-off and review workflows',
                                'PDF reports generated in seconds',
                                'Staff training tracking with expiry alerts',
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-2.5 text-sm text-slate-300">
                                    <Check className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" /> {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="py-20 sm:py-28" data-testid="pricing-section">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold mb-3">Simple, transparent pricing</h2>
                    <p className="text-slate-400 mb-10">One plan. Every feature. No hidden costs.</p>

                    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-8 sm:p-10 relative overflow-hidden">
                        <div className="absolute top-0 right-0 bg-amber-500 text-slate-950 text-xs font-bold px-4 py-1.5 rounded-bl-xl">MOST POPULAR</div>
                        <p className="text-slate-400 text-sm mb-2">Monthly subscription</p>
                        <div className="flex items-baseline justify-center gap-1 mb-6">
                            <span className="text-5xl font-bold text-slate-100">£29.99</span>
                            <span className="text-slate-500">/month</span>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-3 text-left mb-8 max-w-md mx-auto">
                            {[
                                'All 12+ SFBB modules', 'Unlimited staff accounts',
                                'PDF compliance reports', 'Allergen management',
                                'Storage label printing', 'Probe calibration logs',
                                'Delivery acceptance records', 'Training expiry alerts',
                                'Manager dashboards', 'Custom branding',
                            ].map((f) => (
                                <div key={f} className="flex items-center gap-2 text-sm text-slate-300">
                                    <Check className="h-4 w-4 text-emerald-400 shrink-0" /> {f}
                                </div>
                            ))}
                        </div>

                        <Link to="/register">
                            <Button className="brand-btn font-bold text-base h-12 px-10" data-testid="pricing-cta">
                                Get Started Now <ChevronRight className="h-5 w-5 ml-1" />
                            </Button>
                        </Link>
                        <p className="text-xs text-slate-500 mt-4">Cancel anytime. No long-term contracts.</p>
                    </div>
                </div>
            </section>

            {/* Hardware */}
            <section id="hardware" className="py-20 sm:py-28 bg-slate-900/40 border-y border-slate-800/50" data-testid="hardware-section">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-14">
                        <h2 className="text-3xl sm:text-4xl font-bold mb-3">Optional hardware accessories</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">Make recording even faster with Bluetooth devices that work seamlessly with Food Safe Hub. Available to purchase separately.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {hardware.map((h) => (
                            <div key={h.title} className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 flex flex-col" data-testid={`hardware-card-${h.title.toLowerCase().replace(/\s/g, '-')}`}>
                                <div className="text-4xl mb-4">{h.img}</div>
                                <h3 className="text-lg font-semibold text-slate-200 mb-2">{h.title}</h3>
                                <p className="text-sm text-slate-400 mb-4 flex-1">{h.desc}</p>
                                <ul className="space-y-1.5 mb-5">
                                    {h.features.map((f) => (
                                        <li key={f} className="flex items-center gap-2 text-xs text-slate-300">
                                            <Bluetooth className="h-3 w-3 text-blue-400 shrink-0" /> {f}
                                        </li>
                                    ))}
                                </ul>
                                <Button variant="outline" className="w-full border-slate-700 text-slate-300 hover:border-amber-500/40 hover:text-amber-400"
                                    onClick={() => openEnquiry(`Hardware Enquiry — ${h.title}`)} data-testid={`hardware-enquire-${h.title.toLowerCase().replace(/\s/g, '-')}`}>
                                    Enquire <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Disclaimer / Responsibility Section */}
            <section className="py-16">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="bg-slate-900/40 border border-slate-800/60 rounded-xl p-6 sm:p-8">
                        <h3 className="text-base font-semibold text-slate-300 mb-3 flex items-center gap-2">
                            <Shield className="h-5 w-5 text-amber-400" /> Important information
                        </h3>
                        <p className="text-sm text-slate-400 leading-relaxed">
                            Food Safe Hub provides digital tools for the recording and management of food safety compliance data, with processes based on the current Safer Food, Better Business (SFBB) guidance published by the UK Food Standards Agency. However, every food business is different.
                            <strong className="text-slate-300"> It is the responsibility of each business to ensure that food safety policies are appropriate for their specific operation, that those policies are properly implemented, and that all required data is accurately recorded.</strong> Food Safe Hub is a recording and management tool — it does not replace professional food safety advice, and use of the platform does not guarantee compliance with food safety legislation.
                        </p>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 sm:py-28">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <div className="relative rounded-2xl overflow-hidden">
                        <img src={CHEF_IMG} alt="Professional kitchen" className="w-full h-64 sm:h-80 object-cover" />
                        <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[2px]" />
                        <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
                            <h2 className="text-3xl sm:text-4xl font-bold mb-3">Ready to go digital?</h2>
                            <p className="text-slate-300 mb-6 max-w-lg">Join food businesses across the UK using Food Safe Hub to simplify their SFBB compliance.</p>
                            <Link to="/register">
                                <Button className="brand-btn font-bold text-base h-12 px-10" data-testid="final-cta">
                                    Start Your Free Trial <ArrowRight className="h-5 w-5 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} defaultSubject={contactSubject} />
            <ExitIntentModal />

            {/* Footer */}
            <footer className="border-t border-slate-800/50 py-10">
                <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Food Safe Hub" className="h-7" />
                    </div>
                    <div className="flex items-center gap-6 text-sm text-slate-500">
                        <a href="#features" className="hover:text-slate-300">Features</a>
                        <a href="#pricing" className="hover:text-slate-300">Pricing</a>
                        <a href="#hardware" className="hover:text-slate-300">Hardware</a>
                        <button onClick={() => openEnquiry('')} className="hover:text-slate-300" data-testid="footer-contact-us">Contact Us</button>
                        <Link to="/login" className="hover:text-slate-300">Sign In</Link>
                    </div>
                    <p className="text-xs text-slate-600">&copy; {new Date().getFullYear()} Food Safe Hub. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
}
